import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, Search, Loader2 } from "lucide-react";
import { StudentShell } from "../components/StudentShell";
import { useLang } from "../i18n";
import { api } from "../api";

const say = (w) => {
  const u = new SpeechSynthesisUtterance(w);
  u.lang = "en-US"; u.rate = 0.85;
  speechSynthesis.speak(u);
};
const imgFor = (w) => `https://image.pollinations.ai/prompt/${encodeURIComponent(
  `simple colorful flat cartoon illustration of ${w}, white background, for kids`)}?width=320&height=320&nologo=true`;

export default function Dictionary() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [broken, setBroken] = useState({});

  useEffect(() => {
    let stop = false;
    const load = () => api.get("/dictionary").then((r) => {
      if (stop) return;
      const data = Array.isArray(r.data) ? { entries: r.data, pending: 0 } : r.data;
      setItems(data.entries || []);
      setPending(data.pending || 0);
      setLoading(false);
      if ((data.pending || 0) > 0) setTimeout(load, 1500);
    }).catch(() => setLoading(false));
    load();
    return () => { stop = true; };
  }, []);

  const list = useMemo(() => items.filter((it) =>
    it.word.toLowerCase().includes(q.toLowerCase()) || (it.ar || "").includes(q)), [items, q]);

  return (
    <StudentShell>
      <h1 className="text-4xl font-black text-white glow-text">{t("dictionary")}</h1>
      <p className="text-white/65 mt-2">{items.length} {t("words")}
        {pending > 0 && <span className="ms-2 text-xs" data-testid="dict-pending">
          (جارٍ تجهيز {pending} كلمة إضافية...)</span>}</p>
      <div className="relative mt-5 max-w-md">
        <Search size={16} className="absolute top-3 start-3 text-white/45" />
        <input className="input ps-9" placeholder={t("search")} value={q} data-testid="dictionary-search"
          onChange={(e) => setQ(e.target.value)} />
      </div>

      {loading && <div className="mt-10 flex items-center gap-2 text-white/45">
        <Loader2 className="animate-spin" size={18} />{t("loading")}</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        {list.map((it, i) => (
          <motion.div key={it.word} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.02, 0.4) }} className="play-card p-5"
            style={{ borderColor: "var(--play-secondary)" }} data-testid={`dict-card-${it.word}`}>
            <div className="h-28 rounded-2xl bg-white/10 grid place-items-center overflow-hidden">
              {broken[it.word] ? (
                <span className="text-5xl">{it.emoji || "📘"}</span>
              ) : (
                <img src={imgFor(it.word)} alt={it.word} loading="lazy"
                  className="w-full h-full object-cover"
                  onError={() => setBroken((b) => ({ ...b, [it.word]: true }))} />
              )}
            </div>
            <div className="flex items-start justify-between gap-2 mt-4">
              <div>
                <h3 className="text-xl font-black" dir="ltr">{it.word}</h3>
                <div className="text-lg font-bold" style={{ color: "var(--play-primary)" }}>{it.ar || "—"}</div>
              </div>
              <button onClick={() => say(it.word)} data-testid={`dict-say-${it.word}`}
                className="w-9 h-9 shrink-0 rounded-full grid place-items-center text-white"
                style={{ background: "var(--play-secondary)" }}><Volume2 size={15} /></button>
            </div>
            {it.example && <p className="text-xs text-white/65 mt-2" dir="ltr">{it.example}</p>}
          </motion.div>
        ))}
      </div>
      {!loading && !list.length && <p className="text-white/45 mt-8">{t("noData")}</p>}
    </StudentShell>
  );
}
